/*
 * React replacement for the dated Bootstrap-style "more" menu in
 * core/templates/story-reading/part-actions.hbs.
 *
 * Consumes the lifted ActionMenu from @wattpad/web-ui-library and maps each
 * `socialShareHidden` entry to an ActionMenuItem.
 *
 * - Report item: rendered as a button that mirrors `ReportStoryPart.js` —
 *   opens the webview report popup.
 * - All other share items: rendered as anchors that REUSE the legacy
 *   `share-{name}` and `social-share` classes. Backbone's existing event
 *   delegation on the parent StoryReading view (`tap .share-post-to-profile`,
 *   `tap .share-embed`, `tap .social-share`) catches the click, calls
 *   `utils.stopEvent(e)` to suppress the default href navigation, and runs
 *   the right modal/popup logic. This avoids duplicating share-popup logic
 *   in React — PR 5's SocialSharing lift will replace this component entirely.
 *
 * Gated behind the IS_REACT_PART_FOOTER_ACTIONS feature flag in part-actions.hbs.
 */

import React from "react";
import PropTypes from "prop-types";
import { ActionMenu, IconNames } from "@wattpad/web-ui-library";
import { useTrans } from "../hooks";
import { openResponsivePopup } from "../helpers";

// Maps each `socialShareHidden` item's `name` to a library IconName.
// Only items that actually appear in the hidden more-menu are mapped here —
// the brand icons (facebook/twitter/pinterest/tumblr) live in `socialShareVisible`
// and never flow through this component. Unknown values fall through to
// WpShare as a safe visual default.
const ICON_BY_NAME = {
  embed: IconNames.WpEmbed,
  // No WpEmail in the library; the WpSend paper-airplane reads as a
  // "share/send" glyph and is the closest fit. Both `email` and
  // `post-to-profile` intentionally share this icon — see comment below.
  email: IconNames.WpSend,
  // Posting to one's profile is conceptually "sending a comment", which uses
  // the same paper-airplane glyph elsewhere in the app (see the story-part
  // comment-input send button). No dedicated post-to-profile icon exists.
  "post-to-profile": IconNames.WpSend,
  report: IconNames.WpReport
};

function resolveIcon(name) {
  return ICON_BY_NAME[name] || IconNames.WpShare;
}

// Mirrors the class semantics of the legacy `.hbs`:
//   class="share-{{ name }}{{#unless nopopup}} social-share"
// `share-{name}` is always present so item-specific Backbone delegations
// (`tap .share-post-to-profile`, `tap .share-embed`) fire. `social-share`
// is added only when nopopup is false, so generic Backbone delegation
// (`tap .social-share`) catches popup-with-short-link share flows.
function buildShareClassName(item) {
  const base = `share-${item.name}`;
  return item.nopopup ? base : `${base} social-share`;
}

const PartActionsMore = ({ socialShareHidden }) => {
  const { trans } = useTrans();

  if (!socialShareHidden || socialShareHidden.length === 0) {
    return null;
  }

  const options = socialShareHidden.map(item => {
    if (item.name === "report") {
      return {
        key: item.name,
        icon: resolveIcon(item.name),
        label: item.label || trans("Report Story"),
        dataGtmAction: "share:report",
        onClick: event => {
          // Defensive: prevent any parent click handler from interpreting
          // this as a navigation. The button has type="button" so no form
          // submit can occur, but ensure no Backbone delegation acts on it.
          event?.preventDefault?.();
          event?.stopPropagation?.();

          openResponsivePopup({
            url: `/webview/report/storypart/${item.partId}`,
            width: 500,
            height: 700
          });
        }
      };
    }

    return {
      key: item.name,
      icon: resolveIcon(item.name),
      label: item.label,
      href: item.href,
      target: item.nopopup ? undefined : "_blank",
      rel: item.nopopup ? "nofollow" : "nofollow noopener noreferrer",
      dataGtmAction: `share:${item.name}`,
      itemClassName: buildShareClassName(item)
    };
  });

  return (
    <ActionMenu
      options={options}
      aria-label={trans("More options")}
      data-testid="part-actions-more"
    />
  );
};

PartActionsMore.propTypes = {
  socialShareHidden: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      href: PropTypes.string,
      nopopup: PropTypes.bool,
      // Report-only.
      partId: PropTypes.string
    })
  )
};

export default PartActionsMore;
